import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeftIcon as ArrowLeft,
  DatabaseIcon as Database,
  DownloadSimpleIcon as DownloadSimple,
  GraphIcon as Graph,
  FileArchiveIcon as FileArchive,
  FingerprintIcon as Fingerprint,
  GridFourIcon as GridFour,
  TreeStructureIcon as TreeStructure,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  getMetadataCounts,
  listMetadataEntity,
  truncateMetadataEntity,
  importMetadataCsv,
  importMetadataBulkZip,
  getWorkspace,
  listMetadataBackups,
  triggerMetadataBackup,
  restoreMetadataBackup,
} from "@/lib/api";
import {
  ENTITIES,
  PAGE_SIZE,
  formatBytes,
} from "@/lib/metadataConstants";
import EntitySidebar from "@/components/metadata/EntitySidebar";
import EntityTable from "@/components/metadata/EntityTable";
import EntityToolbar from "@/components/metadata/EntityToolbar";
import BackupsStrip from "@/components/metadata/BackupsStrip";
import StaleCheckNotice from "@/components/metadata/StaleCheckNotice";
import { CompareSelectionBar } from "@/components/compare/CompareSelectionBar";
import { useCompareSelection } from "@/hooks/useCompareSelection";
import { staleCheckLink, staleCheckMessage, staleCheckNeedsAttention } from "@/lib/staleCheck";

export default function WorkspaceMetadata() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // Iter 65 — ?entity=<slug>&q=<text> deep links (drawer links) seed the initial view
  const compareSelection = useCompareSelection(); // Iter 63
  const [workspace, setWorkspace] = useState(null);
  const [counts, setCounts] = useState({});
  const [active, setActive] = useState(() => {
    const e = searchParams.get("entity");
    return ENTITIES.some((x) => x.slug === e) ? e : "objects";
  });
  const [page, setPage] = useState(null);
  const [pageNum, setPageNum] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(() => (searchParams.get("q") || "").trim());
  const [backups, setBackups] = useState([]);
  const [snapshotting, setSnapshotting] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [staleCheck, setStaleCheck] = useState(null);
  const bulkInputRef = useRef(null);

  // Iter 50 — surface the disposition stale auto-check an import ran (toast + inline notice).
  const reportStaleCheck = useCallback((check, trigger) => {
    if (!check) return;
    setStaleCheck(check.stale_checked ? { check, trigger } : null);
    const msg = staleCheckMessage(check);
    if (!check.stale_checked || !msg) return;
    const link = staleCheckLink(id, check);
    const level = staleCheckNeedsAttention(check) ? "warning" : "message";
    toast[level](msg, {
      duration: 9000,
      ...(link ? { action: { label: "Open stale dispositions", onClick: () => navigate(link) } } : {}),
    });
  }, [id, navigate]);

  // ---------- Loaders ----------
  const refreshBackups = useCallback(() => {
    listMetadataBackups()
      .then((d) => setBackups(d?.backups || []))
      .catch(() => {});
  }, []);

  const refreshCounts = useCallback(() => {
    getMetadataCounts(id).then(setCounts).catch(() => {});
  }, [id]);

  const loadPage = useCallback(
    async (entitySlug, num, q) => {
      setLoading(true);
      try {
        const data = await listMetadataEntity(id, entitySlug, {
          limit: PAGE_SIZE,
          offset: num * PAGE_SIZE,
          q,
        });
        setPage(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load entity.");
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  // Fetch workspace meta (name, client) for the header.
  useEffect(() => {
    getWorkspace(id).then((d) => setWorkspace(d?.workspace || null));
  }, [id]);

  useEffect(() => {
    refreshCounts();
    refreshBackups();
  }, [refreshCounts, refreshBackups]);

  useEffect(() => {
    loadPage(active, pageNum, query.trim() || undefined);
  }, [active, pageNum, query, loadPage]);

  // ---------- Actions ----------
  const switchEntity = useCallback((slug) => {
    setActive(slug);
    setPageNum(0);
    setQuery("");
  }, []);

  const handleSnapshot = useCallback(async () => {
    setSnapshotting(true);
    try {
      const result = await triggerMetadataBackup();
      toast.success(
        `Snapshot saved: ${result.name} (${formatBytes(result.size_bytes)}).`
      );
      refreshBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snapshot failed.");
    } finally {
      setSnapshotting(false);
    }
  }, [refreshBackups]);

  const handleRestore = useCallback(
    async (backupName) => {
      const confirmMsg =
        `Restore metadata DB from ${backupName}?\n\n` +
        `The CURRENT live DB will be saved as metadata.db.pre-restore.bak so this ` +
        `action is reversible. All workspaces will see the restored data immediately.`;
      if (!window.confirm(confirmMsg)) return;
      const toastId = toast.loading(`Restoring from ${backupName}…`);
      try {
        const result = await restoreMetadataBackup(backupName);
        toast.success(
          `Restored from ${result.restored_from}. ` +
            `Previous DB safed as ${result.pre_restore_snapshot}.`,
          { id: toastId, duration: 6000 }
        );
        refreshBackups();
        refreshCounts();
        loadPage(active, 0, query.trim() || undefined);
        setPageNum(0);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Restore failed.", { id: toastId });
      }
    },
    [refreshBackups, refreshCounts, loadPage, active, query]
  );

  const handleImport = useCallback(
    async (e, fileInputRef, { force = false, fileOverride = null } = {}) => {
      const file = fileOverride || e?.target?.files?.[0];
      if (!file) return;
      const toastId = toast.loading(`${force ? "Re-importing" : "Importing"} ${file.name}…`);
      try {
        const result = await importMetadataCsv(id, active, file, { force });
        if (result.status === "skipped_duplicate") {
          // Iter 38: identical file already imported — nothing changed.
          toast.warning(result.message || "This file was already imported. Nothing was changed.", {
            id: toastId,
            duration: 12000,
            action: {
              label: "Re-import anyway",
              onClick: () => handleImport(null, fileInputRef, { force: true, fileOverride: file }),
            },
          });
          return;
        }
        toast.success(
          `Imported ${result.rows_inserted} rows (${result.rows_skipped} skipped, ${result.errors.length} errors)` +
            (result.file_sha256_short ? ` · SHA-256 ${result.file_sha256_short}…` : "") +
            ".",
          { id: toastId }
        );
        if (result.errors && result.errors.length > 0) {
          console.warn("Import errors:", result.errors);
        }
        reportStaleCheck(result.disposition_stale_check, result.source_command);
        refreshCounts();
        loadPage(active, 0, query.trim() || undefined);
        setPageNum(0);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Import failed.", { id: toastId });
      } finally {
        if (fileInputRef?.current) fileInputRef.current.value = "";
      }
    },
    [id, active, refreshCounts, loadPage, query, reportStaleCheck]
  );

  const handleTruncate = useCallback(async () => {
    if (!window.confirm(`Delete ALL ${active} for this workspace? Cannot be undone.`))
      return;
    try {
      const result = await truncateMetadataEntity(id, active);
      toast.success(`Deleted ${result.deleted} rows from ${active}.`);
      refreshCounts();
      loadPage(active, 0, undefined);
      setQuery("");
      setPageNum(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Truncate failed.");
    }
  }, [id, active, refreshCounts, loadPage]);

  const runBulkImport = useCallback(async (file, { force = false } = {}) => {
    setBulkImporting(true);
    const toastId = toast.loading(`Importing ${file.name}… (all entities, one batch per file)`);
    try {
      const result = await importMetadataBulkZip(id, file, { force });
      const { matched_count, missing_count, error_count, duplicate_count, unknown_count, total_rows_inserted, results } = result;
      const names = (s) => results.filter((r) => r.status === s).map((r) => r.entity).join(", ");
      let summary = `Bulk import complete: ${matched_count} file${matched_count === 1 ? "" : "s"} imported · ${total_rows_inserted} rows.`;
      if (duplicate_count > 0) summary += ` Already imported (skipped): ${names("skipped_duplicate")}.`;
      if (missing_count > 0) summary += ` Missing: ${names("missing")}.`;
      if (error_count > 0) summary += ` Errors: ${names("error")}.`;
      if (unknown_count > 0) summary += ` Unrecognised files ignored: ${result.unknown_files.join(", ")}.`;
      const level = error_count > 0 ? "warning" : duplicate_count > 0 && matched_count === 0 ? "warning" : "success";
      toast[level](summary, {
        id: toastId,
        duration: 9000,
        ...(duplicate_count > 0 && !force
          ? { action: { label: "Re-import duplicates", onClick: () => runBulkImport(file, { force: true }) } }
          : {}),
      });
      reportStaleCheck(result.disposition_stale_check, result.disposition_stale_check?.trigger ? `bulk ZIP (${result.disposition_stale_check.trigger})` : "bulk ZIP");
      refreshCounts();
      loadPage(active, 0, query.trim() || undefined);
      setPageNum(0);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Bulk import failed.",
        { id: toastId }
      );
    } finally {
      setBulkImporting(false);
      if (bulkInputRef.current) bulkInputRef.current.value = "";
    }
  }, [id, active, query, refreshCounts, loadPage, reportStaleCheck]);

  const handleBulkImport = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await runBulkImport(file);
  }, [runBulkImport]);

  // ---- Drag-and-drop ZIP on the whole page ----
  // Uses a depth counter so dragLeave fired on child elements doesn't flicker
  // the overlay off. The dropzone is the entire metadata page so consultants
  // can drop the file anywhere; the overlay confirms they hit the right page.
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);

  const handleDragEnter = useCallback((e) => {
    if (bulkImporting) return;
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  }, [bulkImporting]);

  const handleDragOver = useCallback((e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    if (bulkImporting) return;
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    // Only accept the first .zip file — bulk import is one ZIP per call.
    const zip = files.find((f) => f.name.toLowerCase().endsWith(".zip"));
    if (!zip) {
      toast.error(
        files.length === 1 && files[0].name.toLowerCase().endsWith(".csv")
          ? "For single CSVs use the entity's Import CSV button. Drag a ZIP for bulk import."
          : "Only ZIP files are accepted on this drop zone."
      );
      return;
    }
    await runBulkImport(zip);
  }, [bulkImporting, runBulkImport]);

  const activeEntity = ENTITIES.find((e) => e.slug === active);
  const hasRows = !!(page && page.total > 0);

  return (
    <div
      className="space-y-6 relative"
      data-testid="workspace-metadata-page"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center pointer-events-none border-4 border-dashed border-[var(--ml-primary)] bg-[var(--ml-primary)]/[0.06]"
          data-testid="bulk-drop-overlay"
        >
          <div className="ml-mono text-[14px] uppercase tracking-widest text-[var(--ml-primary)] bg-white px-6 py-4 border border-[var(--ml-primary)]/40 shadow-lg">
            Drop a ZIP to bulk-import all entities (one evidence batch per file)
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to={`/workspaces/${id}`}
            className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] inline-flex items-center gap-1"
            data-testid="back-to-workspace"
          >
            <ArrowLeft size={11} weight="bold" />
            Back to {workspace?.name || "workspace"}
          </Link>
          <h1 className="text-3xl sm:text-4xl font-semibold leading-tight tracking-tight mt-3">
            <Database
              size={26}
              weight="duotone"
              color="#0f62fe"
              className="inline-block mr-2 align-text-bottom"
            />
            Metadata repository
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-2xl">
            Tenant-scoped IBM i catalog data for this workspace. Import CSVs from
            DSPOBJD / DSPFD / DSPFFD / DSPDBR / DSPPGMREF to drive
            impact-analysis, modernization views, and AI-readiness scoring.
          </p>
        </div>
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] text-right">
          Workspace<br />
          <span className="text-[var(--ml-text-primary)]">
            {workspace?.name || "—"}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/workspaces/${id}/metadata/dashboard`}
            data-testid="open-dashboard-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--ml-primary)] text-white text-sm font-medium hover:bg-[var(--ml-primary-hover)] transition-colors"
          >
            <Graph size={14} weight="bold" />
            View modernization dashboard
          </Link>
          <input
            ref={bulkInputRef}
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            onChange={handleBulkImport}
            className="hidden"
            data-testid="bulk-zip-input"
          />
          <button
            type="button"
            onClick={() => bulkInputRef.current?.click()}
            disabled={bulkImporting}
            className="ml-btn-secondary flex items-center gap-1.5"
            data-testid="bulk-import-btn"
            title="Drop a ZIP containing per-entity CSVs (objects.csv, physical-files.csv, …). All 7 importers run in one shot."
          >
            <FileArchive size={14} weight="bold" />
            {bulkImporting ? "Importing ZIP…" : "Bulk import ZIP"}
          </button>
          <Link
            to={`/workspaces/${id}/metadata/source-parser`}
            data-testid="open-source-parser-btn"
            className="ml-btn-secondary flex items-center gap-1.5"
            title="Parse RPG / CL / DDS source files to extract the file-I/O matrix + program-call graph"
          >
            <FileArchive size={14} weight="bold" />
            Source parser
          </Link>
          <a
            href="/modernization-lab-ibmi-evidence-sample-pack.zip"
            download
            data-testid="sample-evidence-pack-link"
            className="ml-btn-secondary flex items-center gap-1.5"
            title="Fictional Acme Co evidence: DSPOBJD / DSPFD / DSPFFD / DSPDBR / DSPPGMREF CSVs plus RPG, CL and DDS source — drop it on Bulk import ZIP and on the Source parser"
          >
            <DownloadSimple size={14} weight="bold" />
            Sample evidence pack
          </a>
          <Link
            to={`/workspaces/${id}/metadata/imports`}
            data-testid="open-import-history-btn"
            className="ml-btn-secondary flex items-center gap-1.5"
            title="Evidence provenance — every import batch with file, SHA-256, command, importer and row counts"
          >
            <Fingerprint size={14} weight="bold" />
            Import history
          </Link>
          <Link
            to={`/workspaces/${id}/dispositions`}
            data-testid="open-dispositions-btn"
            className="ml-btn-secondary flex items-center gap-1.5"
            title="Disposition & treatment matrix — per-object positions proposed from this evidence"
          >
            <GridFour size={14} weight="bold" />
            Dispositions
          </Link>
          <Link
            to={`/workspaces/${id}/metadata/field-dependencies`}
            data-testid="open-field-deps-btn"
            className="ml-btn-secondary flex items-center gap-1.5"
            title="Field-level dependencies — which programs, rules and files touch each field, with evidence"
          >
            <TreeStructure size={14} weight="bold" />
            Field dependencies
          </Link>
        </div>
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          Rollups · journaling · coupling · risk surface
        </span>
      </div>

      <BackupsStrip
        backups={backups}
        snapshotting={snapshotting}
        onSnapshot={handleSnapshot}
        onRestore={handleRestore}
      />

      {staleCheck && (
        <StaleCheckNotice workspaceId={id} check={staleCheck.check} trigger={staleCheck.trigger} onDismiss={() => setStaleCheck(null)} />
      )}

      <div className="grid grid-cols-12 gap-5">
        <EntitySidebar
          active={active}
          counts={counts}
          onSelect={switchEntity}
        />

        <main
          className="col-span-12 md:col-span-9 space-y-4"
          data-testid="metadata-main"
        >
          <EntityToolbar
            entity={activeEntity}
            total={page?.total}
            query={query}
            setQuery={setQuery}
            setPageNum={setPageNum}
            onImport={handleImport}
            onTruncate={handleTruncate}
            hasRows={hasRows}
          />
          <CompareSelectionBar selection={compareSelection} />
          <EntityTable
            active={active}
            entity={activeEntity}
            page={page}
            loading={loading}
            pageNum={pageNum}
            setPageNum={setPageNum}
            workspaceId={id}
            workspaceName={workspace?.name}
            compareSelection={compareSelection}
          />
        </main>
      </div>
    </div>
  );
}
